import { useState, useEffect } from 'react';
import { Spin } from 'antd';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import PasswordResetPage from './components/PasswordResetPage';
import { authAPI, User } from './services/api';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<'login' | 'dashboard' | 'reset-password'>('login');
  const [resetParams, setResetParams] = useState<{ uid: string; token: string } | null>(null);
  const { refreshPermissions } = usePermissions();

  useEffect(() => {
    // URL-dən şifrə reset parametrlərini yoxla
    const urlParams = new URLSearchParams(window.location.search);
    const uid = urlParams.get('uid');
    const token = urlParams.get('token');
    
    if (uid && token) {
      setResetParams({ uid, token });
      setCurrentPage('reset-password');
      setIsLoading(false);
      return;
    }

    // Səhifə yüklənəndə token mövcudluğunu yoxla
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    // Əvvəlcə local storage-də token var mı yoxla
    if (!authAPI.isAuthenticated()) {
      setIsLoggedIn(false);
      setUser(null);
      setIsLoading(false);
      return;
    }

    // "Məni xatırla" funksiyası yoxlanır
    const { tokenManager } = await import('./services/api');
    if (!tokenManager.shouldKeepLoggedIn() && !tokenManager.isRememberMeEnabled()) {
      // Əgər "məni xatırla" seçilməyibsə və müddət keçibsə, logout et
      const tokenExpiry = localStorage.getItem('token_expiry');
      if (tokenExpiry) {
        const expiry = new Date(tokenExpiry);
        const now = new Date();
        if (now > expiry) {
          tokenManager.clearTokens();
          setIsLoggedIn(false);
          setUser(null);
          setIsLoading(false);
          return;
        }
      }
    }

    try {
      const response = await authAPI.getProfile();
      if (response.success && response.user) {
        setIsLoggedIn(true);
        setUser(response.user);
        // İcazələri yüklə
        await refreshPermissions();
      } else {
        // Token keçərsizdir, təmizlə
        setIsLoggedIn(false);
        setUser(null);
      }
    } catch (error: any) {
      setIsLoggedIn(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (userData: User) => {
    setIsLoggedIn(true);
    setUser(userData);
    setCurrentPage('dashboard');
    // Login olduqdan sonra icazələri yüklə
    await refreshPermissions();
    
    // Profil məlumatlarını yenidən yüklə (profile_image daxil olmaqla)
    try {
      const profileResponse = await authAPI.getProfile();
      if (profileResponse.success && profileResponse.user) {
        setUser(profileResponse.user);
      }
    } catch (error) {
      // Xəta olsa da davam et, user artıq login olub
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    // "Məni xatırla" məlumatlarını da təmizlə
    localStorage.removeItem('remember_me');
    localStorage.removeItem('token_expiry');
    setIsLoggedIn(false);
    setUser(null);
    setCurrentPage('login');
  };

  const handleUserUpdate = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const handleBackToLogin = () => {
    setCurrentPage('login');
    setResetParams(null);
    // URL-i təmizlə
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Yüklənir...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {currentPage === 'reset-password' && resetParams ? (
        <PasswordResetPage 
          uid={resetParams.uid} 
          token={resetParams.token}
          onBackToLogin={handleBackToLogin}
        />
      ) : !isLoggedIn || !user ? (
        <LoginPage onLogin={handleLogin} />
      ) : (
        <Dashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
      )}
    </div>
  );
}

function App() {
  return (
    <PermissionProvider>
      <AppContent />
    </PermissionProvider>
  );
}

export default App;
